import React, { useState, useEffect } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  useRecordContext,
  EditButton,
  ListButton,
  TopToolbar,
  useRedirect,
  Labeled,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
  Edit as EditIcon,
  List as ListIcon,
  Memory as VariableIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { dataProvider } from '@/providers/dataProvider';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';

const ShowTitle = () => {
  const record = useRecordContext();
  return record ? `Tabla: ${record.tableName}` : 'Tabla';
};

const ShowActions = () => (
  <TopToolbar>
    <EditButton icon={<EditIcon />} label='Editar' />
    <DeleteWithPasswordButton resource='tables' label='Eliminar' />
    <ListButton icon={<ListIcon />} label='Volver a la Lista' />
  </TopToolbar>
);

const VariablesListSection = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (record?.id) {
      const fetchVariables = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await dataProvider.getList('variables', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: { tableId: record.id },
          });
          setVariables(response.data);
        } catch (err) {
          setError('Error al cargar las variables');
          console.error('Error fetching variables:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchVariables();
    }
  }, [record?.id]);

  if (!record) return null;

  return (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        backgroundColor: 'background.default',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <VariableIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Variables en esta Tabla
          </Typography>
          <Chip
            label={variables.length}
            size='small'
            color='primary'
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : variables.length > 0 ? (
          <Paper
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <List dense>
              {variables.map((variable: any) => (
                <ListItem
                  key={variable.id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <IconButton
                      edge='end'
                      onClick={() => redirect(`/variables/${variable.id}/show`)}
                      size='small'
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <VariableIcon color='primary' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant='body2' fontWeight='medium'>
                        {variable.variableName}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          ID: {variable.id}
                        </Typography>
                        {variable.varType && (
                          <Chip
                            label={variable.varType}
                            size='small'
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 2, textAlign: 'center' }}
          >
            No hay variables asociadas a esta tabla
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export const TablesShow = () => {
  return (
    <Show title={<ShowTitle />} actions={<ShowActions />}>
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <TableIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Detalles de la Tabla
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Información completa de la tabla
                </Typography>
              </Box>
            </Box>

            <SimpleShowLayout>
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <TableIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información de la Tabla
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Labeled label='ID de la Tabla'>
                          <TextField
                            source='id'
                            label='ID de la Tabla'
                            sx={{ mb: 2 }}
                          />
                        </Labeled>

                        <Labeled label='Nombre de la Tabla'>
                          <TextField
                            source='tableName'
                            label='Nombre de la Tabla'
                            sx={{ mb: 2 }}
                          />
                        </Labeled>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Zona y Planta Asociadas */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'fit-content',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          height: 'fit-content',
                          mb: 2,
                        }}
                      >
                        <ZoneIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Ubicación
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <ReferenceField
                        source='zoneId'
                        reference='zones'
                        label='Zona'
                        link='show'
                        sx={{ mb: 2 }}
                      >
                        <Labeled label='Nombre de la Zona'>
                          <TextField source='zoneName' />
                        </Labeled>
                      </ReferenceField>

                      <ReferenceField
                        source='zoneId'
                        reference='zones'
                        label='Planta'
                        link={false}
                      >
                        <ReferenceField
                          source='plantId'
                          reference='plants'
                          link='show'
                        >
                          <Labeled label='Nombre de la Planta'>
                            <TextField source='plantName' />
                          </Labeled>
                        </ReferenceField>
                      </ReferenceField>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Variables Asociadas */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <VariablesListSection />
                </Grid>
              </Grid>
            </SimpleShowLayout>
          </CardContent>
        </Card>
      </Box>
    </Show>
  );
};

export default TablesShow;
